import moment from 'moment';
import store from '../../state';
import { createConsumer } from '@rails/actioncable';
import * as availabilityRequirementSchedulesOperations from '../../state/modules/availability-requirement-schedules/operations';
import * as combinedScheduleOperations from '../../state/modules/combined-schedules/operations';
import { selectDashboardDatetime } from '../../state/modules/dashboards/selectors';

let instance;

// Maps channels to operations that should be called to handle the actual
// subscription
const operations = {
  CombinedSchedulesNotificationsChannel: combinedScheduleOperations.registerForCombinedScheduleUpdates,
  AvailabilityRequirementNotificationsChannel:
    availabilityRequirementSchedulesOperations.registerForAvailabilityRequirementScheduleUpdates
};

export default class DashboardRegistrationManager {
  constructor(dashboardDatetime) {
    if (instance) {
      return instance;
    }

    store.subscribe(this.handleChanged);

    // If an auth token is available to register for regional information, use
    // it. To do so, create a separate actioncable connection which passes in
    // the access token. This will create a new connection with the credentials
    // of a region user, instead of using the connection registered to the
    // current user.
    const token = store.getState().dashboards.dashboardReadAuthToken;
    if (token) {
      const url = `/cable?access_token=${token.access_token}`;
      this.actionCable = createConsumer(url);
    }

    this.dashboardDatetime = dashboardDatetime ? moment(dashboardDatetime) : moment();
    this.locked = false;
    this.subscriptions = {};
    this.batchedUpdates = [];
    instance = this;
  }

  handleChanged = () => {
    const { dateTime, locked } = selectDashboardDatetime(store.getState());

    if (!moment(dateTime).isSame(this.dashboardDatetime) || locked !== this.locked) {
      this.setDashboardDateTime(dateTime, locked);
    }
  };

  registerForAvailabilityRequirementScheduleUpdates = params =>
    this.subscribe('AvailabilityRequirementNotificationsChannel', params);

  registerForCombinedScheduleUpdates = params => this.subscribe('CombinedSchedulesNotificationsChannel', params);

  subscribe(channel, params) {
    const operation = operations[channel];
    if (!operation) throw new Error(`Operation not found for channel ${channel}`);

    if (this.locked) {
      params.start_time = this.dashboardDatetime.format();
    }
    const keyParams = { channel, ...params };
    const key = JSON.stringify(keyParams);
    if (this.subscriptions[key]) return;
    this.subscriptions[key] = {
      subscription: store.dispatch(operation(params, this.actionCable)),
      params
    };
  }

  removeSubscription(key) {
    this.subscriptions[key].subscription.unsubscribe();
    delete this.subscriptions[key];
  }

  // Should be triggered by the dashboard when the dashboardDatetime changes
  setDashboardDateTime(dashboardDatetime, locked) {
    this.dashboardDatetime = moment(dashboardDatetime);
    if (!this.locked && !locked) return;
    this.locked = locked;
    this.resetSubscriptions();
  }

  // Resets subscriptions with the new dashboardDatetime
  resetSubscriptions() {
    let newSubscriptions = [];
    for (let key in this.subscriptions) {
      const params = this.subscriptions[key].params;
      const channel = JSON.parse(key).channel;
      newSubscriptions.push({ params, channel });
      this.removeSubscription(key);
    }
    for (const { params, channel } of newSubscriptions) {
      this.subscribe(channel, params);
    }
  }
}
